import { Fraction } from './fraction'
import type { Inequality, IntegerProblem } from './cuts'

export interface RationalInequalityFrac {
  alpha: Fraction[]
  beta: Fraction
}

export interface RationalProblem {
  A: Fraction[][]
  b: Fraction[]
  c: Fraction[]
  inequalities: RationalInequalityFrac[]
}

/**
 * Parse a string into a Fraction.
 * Supported forms:
 *   - integer: "3", "-2"
 *   - decimal: "0.5", "-1.25"
 *   - fraction: "1/2", "-3/4"
 * Empty string is treated as 0.
 */
export function parseRational(str: string | null | undefined): Fraction {
  const s = (str ?? '').trim()
  if (s === '') return Fraction.zero()

  // Fraction form a/b
  if (s.includes('/')) {
    const parts = s.split('/')
    if (parts.length !== 2) {
      throw new Error(`Invalid fraction string: "${str}"`)
    }
    const num = parts[0].trim()
    const den = parts[1].trim()
    if (num === '' || den === '') {
      throw new Error(`Invalid fraction string: "${str}"`)
    }
    return new Fraction(BigInt(num), BigInt(den))
  }

  // Try integer
  if (/^[+-]?\d+$/.test(s)) {
    return new Fraction(BigInt(s), 1n)
  }

  // Fallback: decimal
  const asNumber = Number(s)
  if (!Number.isFinite(asNumber)) {
    throw new Error(`Invalid numeric string: "${str}"`)
  }
  return Fraction.fromDecimal(asNumber)
}

/**
 * Normalize a rational problem (with Fraction coefficients) into an IntegerProblem
 * by clearing denominators row-wise and for the objective.
 *
 * Returns an IntegerProblem with purely integer A,b,c suitable for cut generation
 * and BnB, along with the original RationalProblem attached to `fracData`.
 *
 * NOTE: This performs best-effort big-int scaling; if coefficients overflow JS
 * number range on conversion, they will be clamped and may affect numerical stability.
 */
export function normalizeRationalProblemToInteger(rp: RationalProblem): IntegerProblem {
  const m = rp.A.length
  const n = rp.A[0]?.length ?? 0

  const A_int: number[][] = []
  const b_int: number[] = []

  // Helper to compute gcd / lcm on bigint
  const gcdBig = (a: bigint, b: bigint): bigint => {
    let x = a < 0n ? -a : a
    let y = b < 0n ? -b : b
    while (y !== 0n) {
      const t = y
      y = x % y
      x = t
    }
    return x
  }
  const lcmBig = (a: bigint, b: bigint): bigint => {
    if (a === 0n || b === 0n) return 0n
    return (a / gcdBig(a, b)) * b
  }

  // Row-wise scaling for constraints
  for (let i = 0; i < m; i++) {
    const row = rp.A[i]
    const beta = rp.b[i]
    // Collect denominators
    let lcmDen = 1n
    for (let j = 0; j < n; j++) {
      const d = row[j].getDenominator()
      lcmDen = lcmBig(lcmDen, d)
    }
    lcmDen = lcmBig(lcmDen, beta.getDenominator())
    if (lcmDen === 0n) lcmDen = 1n

    const rowInts: bigint[] = []
    for (let j = 0; j < n; j++) {
      const num = row[j].getNumerator()
      const den = row[j].getDenominator()
      const factor = lcmDen / den
      rowInts.push(num * factor)
    }
    let betaInt = beta.getNumerator() * (lcmDen / beta.getDenominator())

    // Simplify by gcd over this row (including beta)
    const allCoeffs = [...rowInts, betaInt].map((v) => (v < 0n ? -v : v))
    const nonZero = allCoeffs.filter((v) => v !== 0n)
    if (nonZero.length > 0) {
      let g = nonZero[0]
      for (let k = 1; k < nonZero.length; k++) {
        g = gcdBig(g, nonZero[k])
      }
      if (g > 1n) {
        for (let k = 0; k < rowInts.length; k++) {
          rowInts[k] /= g
        }
        betaInt /= g
      }
    }

    A_int.push(rowInts.map((v) => clampBigIntToNumber(v)))
    b_int.push(clampBigIntToNumber(betaInt))
  }

  // Objective scaling
  let lcmCd = 1n
  for (let j = 0; j < n; j++) {
    lcmCd = lcmBig(lcmCd, rp.c[j].getDenominator())
  }
  if (lcmCd === 0n) lcmCd = 1n
  const c_big: bigint[] = []
  for (let j = 0; j < n; j++) {
    const num = rp.c[j].getNumerator()
    const den = rp.c[j].getDenominator()
    const factor = lcmCd / den
    c_big.push(num * factor)
  }
  // Simplify objective by gcd
  const cAbs = c_big.map((v) => (v < 0n ? -v : v))
  const cNonZero = cAbs.filter((v) => v !== 0n)
  if (cNonZero.length > 0) {
    let g = cNonZero[0]
    for (let k = 1; k < cNonZero.length; k++) {
      g = gcdBig(g, cNonZero[k])
    }
    if (g > 1n) {
      for (let j = 0; j < c_big.length; j++) {
        c_big[j] /= g
      }
    }
  }
  const c_int = c_big.map((v) => clampBigIntToNumber(v))

  // Number inequalities mirroring original Fraction inequalities
  const inequalities: Inequality[] = rp.inequalities.map((ineq) => ({
    alpha: ineq.alpha.map((f) => f.toNumber()),
    beta: ineq.beta.toNumber(),
  }))

  return {
    A: A_int,
    b: b_int,
    c: c_int,
    inequalities,
    fracData: rp,
  }
}

function clampBigIntToNumber(v: bigint): number {
  const asNumber = Number(v)
  if (!Number.isFinite(asNumber)) {
    // Clamp to a large finite value; this should be rare and will be logged in callers if needed.
    return v > 0n ? Number.MAX_SAFE_INTEGER : -Number.MAX_SAFE_INTEGER
  }
  return asNumber
}
